import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { User, Phone, MapPin, MessageSquare, Heart } from 'lucide-react';
import './OnboardingPage.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function OnboardingPage() {
  const [formData, setFormData] = useState({
    // Section 1: You (The Subscriber)
    subscriber_name: '',
    subscriber_email: '',
    subscriber_phone: '',
    subscriber_address: '',
    
    // Section 2: Your Loved One
    parent_full_name: '',
    parent_preferred_name: '',
    parent_phone: '',
    
    // Section 3: About Them
    primary_language: '',
    sensitive_topics: '',
    special_interests: '',
    
    // Section 4: Shipping
    shipping_address: '',
    shipping_city: '',
    shipping_state: '',
    shipping_zip: '',
    shipping_country: 'AUS'
  });
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const { user, token } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      navigate('/login?redirect=/onboarding');
      return;
    }
    // Pre-fill subscriber email from user account
    setFormData(prev => ({
      ...prev,
      subscriber_email: user.email || ''
    }));
    checkOnboardingStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  const checkOnboardingStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/onboarding/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.data.needs_onboarding) {
        navigate('/portal');
      }
    } catch (error) {
      console.error('Error checking onboarding status:', error);
    } finally {
      setChecking(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/onboarding`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Onboarding complete! Welcome to Binding Thread.');
      navigate('/portal');
    } catch (error) {
      console.error('Onboarding error:', error);
      toast.error(error.response?.data?.detail || 'Failed to save information');
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="onboarding-page" data-testid="onboarding-loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="onboarding-page" data-testid="onboarding-page">
      <div className="onboarding-container">
        <div className="onboarding-header">
          <span className="section-label">Final Step</span>
          <h1>Let's Set Up Your Letters</h1>
          <p>
            Help us create a beautiful experience for your loved one.
            All information is kept strictly confidential.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="onboarding-form" data-testid="onboarding-form">
          
          {/* Section 1: You (The Subscriber) */}
          <div className="form-section">
            <div className="section-header">
              <Heart size={20} />
              <h2>Section 1: Your Details</h2>
            </div>
            <p className="form-section-intro">You're the one giving this gift—let us know how to keep you updated.</p>
            
            <div className="form-group">
              <label className="form-label">Your Name</label>
              <input
                type="text"
                name="subscriber_name"
                value={formData.subscriber_name}
                onChange={handleChange}
                className="form-input"
                placeholder="e.g., Sarah Roche"
                required
                data-testid="subscriber-name-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Your Email</label>
              <input
                type="email"
                name="subscriber_email"
                value={formData.subscriber_email}
                onChange={handleChange}
                className="form-input"
                placeholder="e.g., sarah@example.com"
                required
                data-testid="subscriber-email-input"
              />
              <span className="form-hint">Where we'll send the MP3 recording and PDF backup of each letter</span>
            </div>

            <div className="form-group">
              <label className="form-label">Your Phone Number</label>
              <input
                type="tel"
                name="subscriber_phone"
                value={formData.subscriber_phone}
                onChange={handleChange}
                className="form-input"
                placeholder="e.g., 0412 345 678"
                data-testid="subscriber-phone-input"
              />
              <span className="form-hint">Required if you'd like SMS notifications</span>
            </div>

            <div className="form-group">
              <label className="form-label">Your Address</label>
              <input
                type="text"
                name="subscriber_address"
                value={formData.subscriber_address}
                onChange={handleChange}
                className="form-input"
                placeholder="e.g., 45 Beach Road, Surfers Paradise QLD 4217"
                data-testid="subscriber-address-input"
              />
            </div>
          </div>

          {/* Section 2: Your Loved One */}
          <div className="form-section">
            <div className="section-header">
              <User size={20} />
              <h2>Section 2: Your Loved One</h2>
            </div>
            <p className="form-section-intro">Details about the person who will receive the phone calls and have their stories turned into letters.</p>
            
            <div className="form-group">
              <label className="form-label">Full Name</label>
              <input
                type="text"
                name="parent_full_name"
                value={formData.parent_full_name}
                onChange={handleChange}
                className="form-input"
                placeholder="e.g., Margaret Roche"
                required
                data-testid="parent-full-name-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Preferred Name for the Letter</label>
              <input
                type="text"
                name="parent_preferred_name"
                value={formData.parent_preferred_name}
                onChange={handleChange}
                className="form-input"
                placeholder="e.g., Mum, Nana, Dad, Pop"
                required
                data-testid="parent-preferred-name-input"
              />
              <span className="form-hint">How should we address them in the letters?</span>
            </div>

            <div className="form-group">
              <label className="form-label">Primary Phone Number</label>
              <input
                type="tel"
                name="parent_phone"
                value={formData.parent_phone}
                onChange={handleChange}
                className="form-input"
                placeholder="e.g., 0412 345 678"
                required
                data-testid="parent-phone-input"
              />
              <span className="form-hint">We&apos;ll send an SMS to this number letting them know about our service and their welcome pack</span>
              <p className="sms-consent-note" data-testid="sms-consent-note">
                By providing this number, you confirm your Loved One is happy to receive a welcome SMS from us.
              </p>
            </div>
          </div>

          {/* Section 3: About Them */}
          <div className="form-section">
            <div className="section-header">
              <MessageSquare size={20} />
              <h2>Section 3: About Them</h2>
            </div>
            <p className="form-section-intro">Tell us a bit about your loved one to help us make their phone conversations comfortable and meaningful.</p>
            
            <div className="form-group">
              <label className="form-label">Primary Language/Accent</label>
              <input
                type="text"
                name="primary_language"
                value={formData.primary_language}
                onChange={handleChange}
                className="form-input"
                placeholder="e.g., English (Australian), Italian, Greek"
                data-testid="language-input"
              />
              <span className="form-hint">This helps the AI transcription engine tune itself for accuracy</span>
            </div>

            <div className="form-group">
              <label className="form-label">Sensitive Topics to Avoid</label>
              <textarea
                name="sensitive_topics"
                value={formData.sensitive_topics}
                onChange={handleChange}
                className="form-input form-textarea"
                placeholder="e.g., Don't ask about the 1974 floods, Avoid discussing Uncle Tom"
                rows="3"
                data-testid="sensitive-topics-input"
              />
              <span className="form-hint">Are there any topics we should steer away from?</span>
            </div>

            <div className="form-group">
              <label className="form-label">Special Interests</label>
              <textarea
                name="special_interests"
                value={formData.special_interests}
                onChange={handleChange}
                className="form-input form-textarea"
                placeholder="e.g., Gardening, Classical Music, The Gold Coast in the 80s, Cricket"
                rows="3"
                data-testid="interests-input"
              />
              <span className="form-hint">This helps us suggest prompts if they get stuck during the call</span>
            </div>
          </div>

          {/* Section 4: Shipping Address */}
          <div className="form-section">
            <div className="section-header">
              <MapPin size={20} />
              <h2>Section 4: Shipping Address</h2>
            </div>
            <p className="form-section-intro">Where should we send the physical letters and artifacts?</p>
            
            <div className="form-group">
              <label className="form-label">Street Address</label>
              <input
                type="text"
                name="shipping_address"
                value={formData.shipping_address}
                onChange={handleChange}
                className="form-input"
                placeholder="123 Main Street"
                required
                data-testid="address-input"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">City/Suburb</label>
                <input
                  type="text"
                  name="shipping_city"
                  value={formData.shipping_city}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Gold Coast"
                  required
                  data-testid="city-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">State</label>
                <input
                  type="text"
                  name="shipping_state"
                  value={formData.shipping_state}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="QLD"
                  required
                  data-testid="state-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Postcode</label>
                <input
                  type="text"
                  name="shipping_zip"
                  value={formData.shipping_zip}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="4217"
                  required
                  data-testid="zip-input"
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Country</label>
              <select
                name="shipping_country"
                value={formData.shipping_country}
                onChange={handleChange}
                className="form-input"
                data-testid="country-select"
              >
                <option value="AUS">Australia</option>
                <option value="USA">United States</option>
                <option value="CAN">Canada</option>
                <option value="GBR">United Kingdom</option>
                <option value="NZL">New Zealand</option>
              </select>
            </div>
          </div>

          <button
            type="submit"
            className="btn btn-urgent btn-full"
            disabled={loading}
            data-testid="submit-onboarding"
          >
            {loading ? 'Saving...' : 'Complete Setup'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default OnboardingPage;
